import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { dirname, resolve } from "node:path";
import type { DriftIssue } from "../../types.js";
import { mergeWithMarkers, extractCaiSection } from "../../utils/merge.js";

const TOOL_CONFIG_PATHS = [
  "CLAUDE.md",
  "AGENTS.md",
  ".cursorrules",
  ".cursor/rules/cai.mdc",
  ".windsurfrules",
  ".github/copilot-instructions.md",
  ".agent.md",
] as const;

export interface ToolConfigFile {
  path: string;
  content: string;
}

export function getExistingToolConfigs(projectRoot: string): ToolConfigFile[] {
  return TOOL_CONFIG_PATHS
    .map((relativePath) => resolve(projectRoot, relativePath))
    .filter((path) => existsSync(path))
    .map((path) => ({
      path,
      content: readFileSync(path, "utf-8").replace(/\r\n/g, "\n").trimEnd(),
    }));
}

export function checkToolConfigs(projectRoot: string): DriftIssue[] {
  const configs = getExistingToolConfigs(projectRoot);
  if (configs.length < 2) return [];

  const primary = configs[0];
  const primarySection = extractCaiSection(primary.content);
  const issues: DriftIssue[] = [];

  // Effective CAI content from primary: CAI section if present, else full content
  const primaryCaiContent = primarySection ?? primary.content;

  for (const config of configs.slice(1)) {
    const targetSection = extractCaiSection(config.content);
    // Compare primary's effective CAI content against target's CAI section (if present)
    // or full content (fallback for legacy files without markers on both sides)
    const targetCaiContent = targetSection ?? config.content;
    if (targetCaiContent === primaryCaiContent) continue;
    issues.push({
      code: "TOOL_CONFIG_OUT_OF_SYNC",
      severity: "warning",
      file: relativeToProject(projectRoot, config.path),
      line: null,
      message: `Tool config differs from ${relativeToProject(projectRoot, primary.path)}`,
    });
  }

  return issues;
}

export function syncToolConfigs(projectRoot: string): {
  primary: string | null;
  updated: string[];
  skipped: string[];
} {
  const configs = getExistingToolConfigs(projectRoot);
  if (configs.length === 0) {
    return { primary: null, updated: [], skipped: [] };
  }

  const primary = configs[0];
  const updated: string[] = [];
  const skipped: string[] = [relativeToProject(projectRoot, primary.path)];

  // Extract the CAI-managed section from the primary config.
  // If no markers exist yet, treat the whole file as the CAI section (legacy migration).
  const caiContent = extractCaiSection(primary.content) ?? primary.content;

  for (const target of configs.slice(1)) {
    mkdirSync(dirname(target.path), { recursive: true });
    const existing = existsSync(target.path) ? readFileSync(target.path, "utf-8") : null;
    const merged = mergeWithMarkers(existing, caiContent);
    const final = merged.endsWith("\n") ? merged : merged + "\n";
    if (final !== target.content + (target.content.endsWith("\n") ? "" : "\n")) {
      writeFileSync(target.path, final);
    }
    updated.push(relativeToProject(projectRoot, target.path));
  }

  return {
    primary: relativeToProject(projectRoot, primary.path),
    updated,
    skipped,
  };
}

function relativeToProject(projectRoot: string, absolutePath: string): string {
  return absolutePath.slice(projectRoot.length + 1);
}
